import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { Fingerprint, Shield, User, Eye, EyeOff, Lock } from "lucide-react";
import logoImage from "../../image_1752851320826.png";
import { useApp } from "../../contexts/AppContext";
import { useToast } from "../../hooks/use-toast";
import BiometricPrompt from "../../components/BiometricPrompt";
import ForgotPasswordModal from "./ForgotPasswordModal";
import { FormattedMessage, useIntl } from "react-intl";
import { LoginAPI, BiometricLoginAPI } from "./authAPI";
import {
  setBiometricSupported,
  setBiometricEnabled,
  clearError,
  resetTokenExpired,
} from "./authSlice";
import { setLocale } from "../../features/auth/languageSlice";
import { NativeBiometric } from "capacitor-native-biometric";
import { Capacitor } from "@capacitor/core";
import { Switch } from "../../components/ui/switch";
function AuthPage() {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);
  const [biometryType, setBiometryType] = useState(null);
  const intl = useIntl();
  const locale = useSelector((state) => state.language.locale || "fr");
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { login } = useApp();
  const handleToggle = (checked) => {
    const newLocale = checked ? "fr" : "en";
    localStorage.setItem("locale", newLocale);
    dispatch(setLocale(newLocale));
  };
  const {
    loader,
    biometricLoader,
    biometricSupported,
    biometricEnabled,
    error,
    isAuthenticated,
    token,
    user,
    tokenExpired,
  } = useSelector((state) => state.auth);

  // Initialize biometric flag
  useEffect(() => {
    const initializeBiometricFlag = () => {
      const flag = localStorage.getItem("biometricEnabled");
      if (flag !== "true" && flag !== "false") {
        localStorage.setItem("biometricEnabled", "false");
        dispatch(setBiometricEnabled(false));
      } else {
        dispatch(setBiometricEnabled(flag === "true"));
      }
    };
    initializeBiometricFlag();
  }, [dispatch]);

  // Check biometric support and stored credentials
  useEffect(() => {
    const checkBiometric = async () => {
      const flag = localStorage.getItem("biometricEnabled");
      if (flag != null) {
        try {
          if (Capacitor.isNativePlatform()) {
            const result = await NativeBiometric.isAvailable();
            if (result.isAvailable) {
              dispatch(setBiometricSupported(true));
              setBiometryType(result.biometryType);
            }
            try {
              await NativeBiometric.getCredentials({ server: "TechDispatch" });
              dispatch(setBiometricEnabled(true));
              localStorage.setItem("biometricEnabled", "true");
            } catch {
              dispatch(setBiometricEnabled(false));
              localStorage.setItem("biometricEnabled", flag || "false");
            }
          } else {
            if (
              window.PublicKeyCredential &&
              (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
            ) {
              dispatch(setBiometricSupported(true));
              setBiometryType("web");
              dispatch(setBiometricEnabled(flag === "true"));
            }
          }
        } catch {
          dispatch(setBiometricSupported(false));
          dispatch(setBiometricEnabled(false));
          localStorage.setItem("biometricEnabled", "false");
        }
      }
    };
    checkBiometric();
  }, [dispatch]);

  // Handle successful authentication
  useEffect(() => {
    if (isAuthenticated && token && user && loginCredentials) {
      if (biometricSupported && !biometricEnabled) {
        setShowBiometricPrompt(true);
      }
    }
  }, [
    isAuthenticated,
    token,
    user,
    loginCredentials,
    biometricSupported,
    biometricEnabled,
  ]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast({
        title: intl.formatMessage({
          id: "auth.failed",
          defaultMessage: "Authentication Failed",
        }),
        description: intl.formatMessage({
          id: "auth.failedDescription",
          defaultMessage: "Authentication Failed",
        }),
        variant: "error",
      });
      dispatch(clearError());
    }
  }, [error, toast, dispatch, intl]);

  // Handle token expiration
  useEffect(() => {
    if (tokenExpired) {
      toast({
        title: intl.formatMessage({
          id: "auth.sessionExpired",
          defaultMessage: "Session Expired",
        }),
        description: intl.formatMessage({
          id: "auth.sessionExpiredDescription",
          defaultMessage: "Your session has expired. Please log in again.",
        }),
        variant: "destructive",
      });
      dispatch(resetTokenExpired());
    }
  }, [tokenExpired, toast, dispatch, intl]);

  // const handleLanguageChange = (e) => {
  //   const newLocale = e.target.value;
  //   dispatch(setLocale(newLocale));
  //   localStorage.setItem("locale", newLocale);
  // };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!employeeId.trim() || !password.trim()) {
      toast({
        title: intl.formatMessage({
          id: "auth.error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "auth.missingCredentials",
          defaultMessage: "Please enter both Employee ID and Password",
        }),
        variant: "error",
      });
      return;
    }

    setLoginCredentials({
      employeeId: employeeId.trim(),
      password: password.trim(),
    });

    if (biometricSupported && !biometricEnabled) {
      setShowBiometricPrompt(true);
    } else {
      dispatch(
        LoginAPI({
          UserName: employeeId.trim(),
          password: password.trim(),
        })
      );
    }
  };

  const handleBiometricLogin = async () => {
    try {
      await NativeBiometric.verifyIdentity({
        reason: intl.formatMessage({
          id: "auth.biometricReason",
          defaultMessage: "Login with biometrics",
        }),
        title: intl.formatMessage({
          id: "auth.biometricTitle",
          defaultMessage: "Biometric Login",
        }),
        subtitle: intl.formatMessage({
          id: "auth.biometricSubtitle",
          defaultMessage: "Use your biometric to login",
        }),
      });
      dispatch(BiometricLoginAPI());
    } catch (error) {
      toast({
        title: intl.formatMessage({
          id: "auth.biometricLoginFailed",
          defaultMessage: "Biometric Login Failed",
        }),
        description:
          error.message ||
          intl.formatMessage({
            id: "auth.biometricLoginFailedDesc",
            defaultMessage: "Please try again or use your credentials",
          }),
        variant: "error",
      });
    }
  };

  const handleBiometricPromptClose = (success) => {
    setShowBiometricPrompt(false);
    if (success) {
      localStorage.setItem("biometricEnabled", "true");
      dispatch(setBiometricEnabled(true));
      dispatch(
        LoginAPI({
          UserName: loginCredentials.employeeId,
          password: loginCredentials.password,
        })
      );
      toast({
        title: intl.formatMessage({
          id: "auth.biometricEnabledTitle",
          defaultMessage: "Success",
        }),
        description: intl.formatMessage({
          id: "auth.biometricEnabledDesc",
          defaultMessage: "Biometric login enabled!",
        }),
        variant: "success",
      });
    } else {
      dispatch(
        LoginAPI({
          UserName: loginCredentials.employeeId,
          password: loginCredentials.password,
        })
      );
    }
    setLoginCredentials(null);
    setPassword("");
  };

  const getBiometricButtonText = () => {
    if (biometryType === "FACE_ID") {
      return intl.formatMessage({
        id: "auth.loginWithFaceId",
        defaultMessage: "Login with Face ID",
      });
    }
    if (biometryType === "TOUCH_ID") {
      return intl.formatMessage({
        id: "auth.loginWithTouchId",
        defaultMessage: "Login with Touch ID",
      });
    }
    if (biometryType === "FINGERPRINT") {
      return intl.formatMessage({
        id: "auth.loginWithFingerprint",
        defaultMessage: "Login with Fingerprint",
      });
    }
    return intl.formatMessage({
      id: "auth.useBiometric",
      defaultMessage: "Login with Biometrics",
    });
  };

  return (
    <>
      {/* Language selector */}

      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="w-full max-w-md">
          <Card className="shadow-xl transition-theme">
            <CardContent className="!p-8">
              <div className="!mb-10 flex justify-end">
                <div className="flex items-center gap-3">
                  <span
                    className={
                      locale === "en" ? "font-semibold" : "text-gray-500"
                    }
                  >
                    EN
                  </span>

                  <Switch
                    checked={locale === "fr"}
                    onCheckedChange={handleToggle}
                  />

                  <span
                    className={
                      locale === "fr" ? "font-semibold" : "text-gray-500"
                    }
                  >
                    FR
                  </span>
                </div>
              </div>

              <div className="text-center mb-8">
                <div className="w-20 h-20 custom-bg dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors p-2">
                  <img
                    src={logoImage}
                    alt="TechDispatch Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h1 className="text-2xl font-bold mb-2">
                  <FormattedMessage
                    id="auth.welcome"
                    defaultMessage="Welcome to TechDispatch"
                  />
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  <FormattedMessage
                    id="auth.chooseMethod"
                    defaultMessage="Choose your authentication method"
                  />
                </p>
              </div>

              {/* Biometric Login Button */}
              {biometricEnabled && (
                <>
                  <Button
                    onClick={handleBiometricLogin}
                    className="mb-4 w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg"
                    disabled={loader || biometricLoader}
                  >
                    {biometricLoader ? (
                      <div className="flex items-center">
                        <Skeleton className="h-5 w-5 mr-2 rounded-full animate-spin bg-white/20" />
                        <FormattedMessage
                          id="auth.authenticating"
                          defaultMessage="Authenticating..."
                        />
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Fingerprint className="h-6 w-6 mr-2" />
                        {getBiometricButtonText()}
                      </div>
                    )}
                  </Button>
                  <div className="text-center mb-4 text-gray-500">
                    <FormattedMessage
                      id="auth.orLoginWith"
                      defaultMessage="Or login with username/password"
                    />
                  </div>
                </>
              )}

              {/* Login Form */}
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="employeeId">
                    <FormattedMessage
                      id="auth.employeeId"
                      defaultMessage="Employee ID"
                    />
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="employeeId"
                      type="text"
                      placeholder={intl.formatMessage({
                        id: "auth.enterEmployeeId",
                        defaultMessage: "Enter your Employee ID",
                      })}
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      disabled={loader || biometricLoader}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">
                    <FormattedMessage
                      id="auth.password"
                      defaultMessage="Password"
                    />
                  </Label>
                  <div className="relative">
                    {/* Lock icon on the left */}
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

                    {/* Password input */}
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={intl.formatMessage({
                        id: "auth.enterPassword",
                        defaultMessage: "Enter your password",
                      })}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loader || biometricLoader}
                      className="pl-10 pr-10"
                    />

                    {/* Eye toggle icon on the right */}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loader || biometricLoader}
                  variant="outline"
                >
                  {loader ? (
                    <div className="flex items-center">
                      <Skeleton className="h-4 w-4 mr-2 rounded-full animate-spin" />
                      <FormattedMessage
                        id="auth.loggingIn"
                        defaultMessage="Logging in..."
                      />
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      <FormattedMessage
                        id="auth.login"
                        defaultMessage="Login"
                      />
                    </div>
                  )}
                </Button>

                {/* Forgot Password Link */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                    disabled={loader || biometricLoader}
                  >
                    <FormattedMessage
                      id="auth.forgotPassword"
                      defaultMessage="Forgot Password?"
                    />
                  </button>
                </div>
              </form>

              {/* Biometric Setup Info */}
              {!biometricEnabled && biometricSupported && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-700 dark:text-blue-300 text-center">
                    <FormattedMessage
                      id="auth.biometricInfo"
                      defaultMessage="After login, you can enable biometric authentication for faster future logins."
                    />
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Biometric Setup Prompt */}
      {showBiometricPrompt && loginCredentials && (
        <BiometricPrompt
          credentials={loginCredentials}
          onClose={handleBiometricPromptClose}
        />
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </>
  );
}

export default AuthPage;
